import type { ClientLocationBaseData, ClientLocationWithLevel } from "api-types/locations/locationsResponses";
import { getBuilderLocationBaseDataURL, getBuilderLocationChildrenURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useUserContext } from "lib/state/UserContextProvider";
import type { LocationSummaryScope } from "./locationSummaryTypes";

export type LocationLeafState = "leaf" | "parent" | "unknown";

export function useLocationContext(scope: LocationSummaryScope): {
  leafState: LocationLeafState;
  lotsPossible: boolean;
  loading: boolean;
} {
  const { hasPermission } = useUserContext();
  const locationId = scope.kind === "location" ? scope.locationId : undefined;
  const canViewChildren = locationId ? hasPermission("VIEW_CHILD_LOCATIONS", { resourceId: locationId }) : false;
  const baseDataUrl = locationId ? getBuilderLocationBaseDataURL({ builderId: scope.builderId, locationId }) : "";
  const childrenUrl = locationId ? getBuilderLocationChildrenURL({ builderId: scope.builderId, locationId }) : "";
  const dependencies = [scope.kind === "location", scope.builderId, locationId];
  const baseData = useFetch<ClientLocationBaseData>(baseDataUrl, dependencies, {
    handleErrorLocally: true,
  });
  const children = useFetch<ClientLocationWithLevel[]>(childrenUrl, [...dependencies, canViewChildren], {
    handleErrorLocally: true,
  });

  if (scope.kind === "builder") {
    return { leafState: "parent", lotsPossible: true, loading: false };
  }

  let leafState: LocationLeafState = "unknown";
  if (baseData.data) {
    leafState = baseData.data.location.childrenCount === 0 ? "leaf" : "parent";
  }

  return {
    leafState,
    // Fails open on missing data or fetch errors: better to show the lot card than silently hide it.
    lotsPossible:
      !baseData.data ||
      !canViewChildren ||
      Boolean(children.error) ||
      baseData.data.location.disableLots === false ||
      children.data?.some((child) => child.lotsCount > 0) === true,
    // useFetch reports loading forever on a skipped fetch (no null-key check at fetcher.ts:99), so
    // the permission guard is what stops an unpermitted user sitting on a permanent spinner.
    loading: baseData.loading || (canViewChildren && children.loading),
  };
}
